import { useEffect, useRef, useState } from 'react';
import { Location } from '@client/router/compat';
import { useRouting } from './useRouting';

const toPath = (location: Location) =>
  `${location.pathname}${location.search}${location.hash}`;

type UnsavedChangesGuard = {
  blockedPath: string | null;
  dismiss: () => void;
  leave: () => void;
};

export const useUnsavedChangesGuard = (
  hasUnsavedChanges: boolean,
): UnsavedChangesGuard => {
  const routing = useRouting();
  const [blockedPath, setBlockedPath] = useState<string | null>(null);

  const isUnsaved = useRef(hasUnsavedChanges);
  const allowNextLeave = useRef(false);
  isUnsaved.current = hasUnsavedChanges;

  useEffect(() => {
    if (!routing) return;

    const handleRouteLeave = (nextLocation?: Location) => {
      if (!nextLocation) return undefined;
      if (allowNextLeave.current) {
        allowNextLeave.current = false;
        return undefined;
      }
      if (!isUnsaved.current) return undefined;

      setBlockedPath(toPath(nextLocation));
      return false;
    };

    const removeHook = routing.router.setRouteLeaveHook(
      routing.route,
      handleRouteLeave,
    );
    return () => removeHook?.();
  }, [routing]);

  useEffect(() => {
    const warnOnUnload = (ev: BeforeUnloadEvent) => {
      if (!isUnsaved.current) return;
      ev.preventDefault();
      ev.returnValue = '';
    };

    window.addEventListener('beforeunload', warnOnUnload);
    return () => window.removeEventListener('beforeunload', warnOnUnload);
  }, []);

  return {
    blockedPath,
    dismiss: () => setBlockedPath(null),
    leave: () => {
      allowNextLeave.current = true;
      setBlockedPath(null);
      if (blockedPath) routing?.router.push(blockedPath);
    },
  };
};
